// Zweck: Buchungsprüfung ohne eigene Speicherung oder Übertragung. Stand: 21.10.2026.
import { WarehouseSteps } from './WarehousePresentation';
import type {
  SimOperation,
  SimState,
  SimQueueEntry,
} from '../interfaces/deviceSimulation';
interface Props {
  draft: SimOperation;
  snapshot: SimState | null;
  ledger: SimQueueEntry[];
  titles: Record<string, string>;
  conditions: Record<string, string>;
  offline: boolean;
  connected: boolean;
  busy: boolean;
  onEdit: () => void;
  onSubmit: () => void;
}
export default function WarehouseDeviceReview({
  draft,
  snapshot,
  ledger,
  titles,
  conditions,
  offline,
  connected,
  busy,
  onEdit,
  onSubmit,
}: Props) {
  const article = (id: string) => snapshot?.articles.find((a) => a.id === id);
  const locationName = (id: string) =>
    snapshot?.locations.find((l) => l.id === id)?.name ?? id;
  const metadata = ['block', 'release', 'serial', 'note'].includes(draft.kind);
  const online = { current: !offline && connected };
  return (
    <section className="wh-review" aria-labelledby="wh-review-title">
      <WarehouseSteps
        step={draft.kind === 'note' ? 2 : 3}
        material
        labels={draft.kind === 'note' ? ['Notiz', 'Prüfen'] : undefined}
      />
      <button className="wh-back wh-review-back" onClick={onEdit}>
        Angaben bearbeiten
      </button>
      <h2 id="wh-review-title">
        {draft.kind === 'note'
          ? 'Notiz'
          : draft.kind === 'issue'
            ? 'Ausgabe'
            : draft.kind === 'return'
              ? 'Rückgabe'
              : titles[draft.kind]}{' '}
        prüfen
      </h2>
      <p className="wh-field-hint">
        {metadata
          ? 'Änderung noch nicht bestätigt. Stimmen die Geräteangaben?'
          : 'Noch nichts gebucht. Stimmen Artikel, Mengen und Geräte?'}
      </p>
      <p className="wh-context">
        {locationName(draft.locationId)} ·{' '}
        {snapshot?.projects.find((p) => p.id === draft.projectId)?.name ??
          'Ohne Auftrag'}{' '}
        {draft.projectId}
      </p>
      {draft.sourceDocument && (
        <p className="wh-context">
          Beleg (älterer Entwurf): {draft.sourceDocument.number} · Fassung{' '}
          {draft.sourceDocument.version}
        </p>
      )}
      {draft.lines.map((l) => (
        <div className="wh-review-line" key={l.articleId}>
          <div className="wh-review-item">
            <div>
              <strong>
                {snapshot?.articles.find((a) => a.id === l.articleId)?.name}
              </strong>
              <p className="wh-field-hint">
                Artikelnr. {article(l.articleId)?.number ?? l.articleId}
              </p>
            </div>
            <strong className="wh-review-quantity">
              {l.quantity} {article(l.articleId)?.unit}
            </strong>
          </div>
          {draft.devices
            .filter((d) => d.articleId === l.articleId)
            .map((d) => (
              <p key={d.id}>
                {d.serial ? 'Seriennummer' : 'Vorläufige Kennzeichnung'}:{' '}
                <strong>{d.serial ?? d.label}</strong> ·{' '}
                {conditions[d.condition]}
              </p>
            ))}
        </div>
      ))}
      {metadata &&
        draft.devices.map((d) => (
          <div className="wh-review-line" key={d.id}>
            <strong>{d.serial ?? d.label}</strong>
            <p>
              {draft.kind === 'release'
                ? 'Nach Freigabe wieder verwendbar'
                : conditions[d.condition]}
            </p>
          </div>
        ))}
      {draft.devices
        .filter((d) => !!d.note)
        .map((d) => (
          <p key={'note-' + d.id}>
            {d.serial ?? d.label}: {d.note}
          </p>
        ))}
      {draft.kind === 'correction' && (
        <p className="wh-notice">
          Diese {draft.devices.length} Geräte werden zurückgebucht.{' '}
          {(
            ledger.find((q) => q.operation.requestId === draft.correctsId)
              ?.operation.lines ?? []
          )
            .filter(
              (l) =>
                !snapshot?.articles.find((a) => a.id === l.articleId)?.tracked,
            )
            .map(
              (l) =>
                `${l.quantity} ${article(l.articleId)?.unit ?? ''} ${snapshot?.articles.find((a) => a.id === l.articleId)?.name ?? l.articleId}`,
            )
            .join(', ') || 'Andere Positionen'}{' '}
          bleiben gebucht.
        </p>
      )}
      {draft.reason && <p>Begründung: {draft.reason}</p>}
      <div className="wh-review-confirm">
        <strong>Angaben bestätigen</strong>
        <p>
          {draft.kind === 'issue'
            ? 'Bestätige nur tatsächlich bereitgestelltes Material. Geräte erst nach bestätigter One360-Buchung übergeben.'
            : 'Bestätige die tatsächlich erfassten Angaben.'}
        </p>
        {(offline || !connected) && (
          <p className="wh-notice">
            Ohne Verbindung: Die Liste bleibt auf diesem Gerät. Geräte noch
            nicht übergeben.
          </p>
        )}
        {!online.current && (
          <p>
            Nach deiner Bestätigung wird dieser Vorgang bei Verbindung
            automatisch gebucht.
          </p>
        )}
        <button className="wh-primary" disabled={busy} onClick={onSubmit}>
          {!online.current
            ? 'Bei Verbindung buchen'
            : draft.kind === 'issue'
              ? 'Ausgabe buchen'
              : 'Buchung bestätigen'}
        </button>
      </div>
    </section>
  );
}
